import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Users, UserPlus, Shield, ShieldOff, Trash2, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserManagement = () => {
  const { language } = useLanguage();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'admin'
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error(language === 'en' ? 'Failed to fetch users' : 'Gagal mengambil pengguna');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'admin'
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/auth/register`, formData);
      toast.success(language === 'en' ? 'User created!' : 'Pengguna dibuat!');
      setDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creating user');
    }
  };

  const handleToggleStatus = async (userId) => {
    if (userId === currentUser?.id) {
      toast.error(language === 'en' ? 'Cannot modify your own status' : 'Tidak dapat mengubah status sendiri');
      return;
    }
    
    try {
      await axios.put(`${API}/users/${userId}/status`);
      toast.success(language === 'en' ? 'User status updated!' : 'Status pengguna diperbarui!');
      fetchUsers();
    } catch (error) {
      toast.error(language === 'en' ? 'Failed to update status' : 'Gagal memperbarui status');
    }
  };

  const handleDelete = async (userId) => {
    if (userId === currentUser?.id) {
      toast.error(language === 'en' ? 'Cannot delete yourself' : 'Tidak dapat menghapus diri sendiri');
      return;
    }
    
    if (!window.confirm(language === 'en' ? 'Delete this user?' : 'Hapus pengguna ini?')) return;
    
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success(language === 'en' ? 'User deleted!' : 'Pengguna dihapus!');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'en' ? 'en-US' : 'id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <AdminLayout title={language === 'en' ? 'User Management' : 'Manajemen Pengguna'}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={language === 'en' ? 'User Management' : 'Manajemen Pengguna'}>
      <div className="space-y-6" data-testid="user-management-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <p className="text-stone-600">
            {language === 'en' 
              ? 'Manage admin users who can access the dashboard.'
              : 'Kelola pengguna admin yang dapat mengakses dashboard.'}
          </p>
          <Button onClick={openCreateDialog} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-user-btn">
            <UserPlus className="w-4 h-4 mr-2" />
            {language === 'en' ? 'Add User' : 'Tambah Pengguna'}
          </Button>
        </div>

        {/* Users List */}
        <Card data-testid="users-list-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-orange-500" />
              {language === 'en' ? 'Admin Users' : 'Pengguna Admin'} ({users.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {users.length > 0 ? (
              <div className="space-y-4">
                {users.map((user, index) => (
                  <div 
                    key={user.id}
                    className="flex items-center justify-between p-4 bg-stone-50 rounded-lg"
                    data-testid={`user-item-${index}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        user.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-200 text-stone-500'
                      }`}>
                        <span className="text-lg font-bold">
                          {user.name?.charAt(0).toUpperCase() || 'U'}
                        </span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-stone-900">{user.name}</h4>
                          {user.id === currentUser?.id && (
                            <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                              {language === 'en' ? 'You' : 'Anda'}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-stone-500">{user.email}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            user.is_active 
                              ? 'bg-emerald-100 text-emerald-700' 
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {user.is_active 
                              ? (language === 'en' ? 'Active' : 'Aktif') 
                              : (language === 'en' ? 'Inactive' : 'Nonaktif')}
                          </span>
                          <span className="text-xs text-stone-400 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(user.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleToggleStatus(user.id)}
                        disabled={user.id === currentUser?.id}
                        title={user.is_active ? 'Deactivate' : 'Activate'}
                        data-testid={`toggle-status-${index}`}
                      >
                        {user.is_active ? (
                          <ShieldOff className="w-4 h-4 text-amber-600" />
                        ) : (
                          <Shield className="w-4 h-4 text-emerald-600" />
                        )}
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDelete(user.id)} 
                        className="text-red-600 hover:text-red-700"
                        disabled={user.id === currentUser?.id}
                        data-testid={`delete-user-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-stone-500">
                {language === 'en' ? 'No users found' : 'Tidak ada pengguna ditemukan'}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add User Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle>
              {language === 'en' ? 'Add Admin User' : 'Tambah Pengguna Admin'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>{language === 'en' ? 'Full Name' : 'Nama Lengkap'} *</Label>
              <Input
                required
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="John Doe"
                data-testid="new-user-name"
              />
            </div>

            <div>
              <Label>{language === 'en' ? 'Email Address' : 'Alamat Email'} *</Label>
              <Input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="user@example.com"
                data-testid="new-user-email"
              />
            </div>

            <div>
              <Label>{language === 'en' ? 'Password' : 'Kata Sandi'} *</Label>
              <Input
                type="password"
                required
                minLength={6}
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                placeholder="••••••••"
                data-testid="new-user-password"
              />
              <p className="text-xs text-stone-500 mt-1">
                {language === 'en' ? 'Minimum 6 characters' : 'Minimal 6 karakter'}
              </p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                {language === 'en' ? 'Cancel' : 'Batal'}
              </Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-user-btn">
                {language === 'en' ? 'Create User' : 'Buat Pengguna'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default UserManagement;
